<template>
  <div class="form-item-container">
    <div class="form-item-header">
      <v-btn class="gorate-bg cursor-default" :icon="getFormItemIconIndex" height="30" variant="text"
        width="30"></v-btn>
      <div class="question">
        {{ formItem.question_lang1 }}
      </div>
    </div>
    <!-- FACES -->
    <div v-if="getRatingTypeName(formItem.rating_type_id) === 'FACES' && isFaceRatingDetail" class="faces-item-body">
      <div class="overall-info-container"><span class="font-weight-black pr-1">{{ $t('i18n.overall') }}:</span>{{
        getAverageFacePercentage() }}%</div>
      <div class="all-faces-container">
        <div class="single-face-container" v-for="(singleFaceStats, index) in getFacesVoteDistribution()" :key="index">
          <v-progress-circular :model-value="singleFaceStats.percentage" :rotate="360" :size="60" :width="4"
            :color="getFaceColor(index)">
            <!-- <v-icon :icon="getEmoticon(index)" size="x-large"></v-icon> -->
            <component :is="getEmoticon(index)"></component>
          </v-progress-circular>
          <div class="percentage-count-container pt-2">
            <div class="response-count">{{ singleFaceStats.response_count }}</div>
            <div class="percentage">({{ singleFaceStats.percentage.toFixed(1) }}%)</div>
          </div>
        </div>
      </div>
      <div class="submission-info-container">
        {{ $t('i18n.submissions') }}: {{ getTotalResponses(formItemIndex - 1) }}
      </div>
    </div>
    <!-- ESTRELAS -->
    <div v-if="getRatingTypeName(formItem.rating_type_id) === 'STARS'" class="stars-item-body">
      <div class="stats-summary">
        <div class="text-h3 font-weight-black text-center">
          {{ getFormItemAvgRate(formItemIndex - 1) }}
          <span class="text-h6 ml-n3">/5</span>
        </div>
        <v-rating class="star-rating mb-1" :model-value="getFormItemAvgRate(formItemIndex - 1)" :size="22"
          color="yellow-darken-3" half-increments readonly></v-rating>
        <div class="text-center">{{ getTotalResponses(formItemIndex - 1) }} {{ $t('i18n.submissions') }}</div>
      </div>
      <div class="stats-detailed">
        <div class="stats-detailed-label">
          <div v-for="step in [...Array(getRatingTypeStepsNumber(formItem.rating_type_id)).keys()].reverse()"
            :key="step">
            {{ step + 1 }} {{ $t('i18n.star') }}<span v-if="(step + 1) > 1">s</span>
          </div>
        </div>
        <div v-if="isStarRatingDetail" class="stats-detailed-bars">
          <div v-for="([starNumber, numberOfVotes]) in reversedStarsVoteDistribution()" :key="starNumber">
            <v-progress-linear color="#ffd139" rounded :max="getTotalResponses(formItemIndex - 1)"
              :model-value="numberOfVotes" :height="8"></v-progress-linear>
          </div>
        </div>
        <div v-if="isStarRatingDetail" class="stats-detailed-numbers">
          <div v-for="([starNumber, numberOfVotes]) in reversedStarsVoteDistribution()" :key="starNumber">
            {{ numberOfVotes }} {{ getStarPercentage(numberOfVotes, formItemIndex - 1) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { FormItem, RatingType, FaceRatingStatsDetail, FormStats, SingleFaceStats, StarRatingStatsDetail } from '@/utils/DataTypes'
import { useRouter } from 'vue-router'
import EmoticonFace4 from '@/components/icons/EmoticonFace4.vue'
import EmoticonFace3 from '@/components/icons/EmoticonFace3.vue'
import EmoticonFace2 from '@/components/icons/EmoticonFace2.vue'
import EmoticonFace1 from '@/components/icons/EmoticonFace1.vue'
import { useAppStore } from '@/stores/appStore'

export default defineComponent({
  name: 'RatingStats',
  setup () {
    const router = useRouter()
    const appStore = useAppStore()
    return {
      router,
      appStore
    }
  },
  async mounted () {
    this.ratingTypesList = this.appStore.getAppState.ratingTypesList
  },
  components: {
    EmoticonFace4,
    EmoticonFace3,
    EmoticonFace2,
    EmoticonFace1
  },
  props: {
    stats: {
      type: Object as PropType<FormStats>,
      required: true,
      default: null
    },
    formItem: {
      type: Object as PropType<FormItem>,
      required: true,
      default: null
    },
    formItemIndex: {
      type: Number,
      required: true,
      default: -1
    },
    formName: {
      type: String,
      required: false,
      default: 'N/A'
    }
  },
  data () {
    return {
      ratingTypesList: [] as RatingType[]
    }
  },
  methods: {
    /* ==============STARS RATING ============== */
    getFormItemAvgRate (index: number): number | string {
      const detail = this.stats?.data?.[index]?.detail

      if (!detail) {
        return 'N/A'
      }

      if ('average_rating' in detail) {
        return detail.average_rating
      }

      return 'N/A'
    },
    getTotalResponses (index: number): number | string {
      return this.stats?.data?.[index]?.total_responses ?? 'N/A'
    },
    getStarsVoteDistribution (): { [key: number]: number } {
      if (this.isStarRatingDetail) {
        return (this.stats.data[this.formItemIndex - 1].detail as StarRatingStatsDetail).vote_distribution
      }
      return {}
    },
    reversedStarsVoteDistribution (): Array<[number, number]> {
      if (this.isStarRatingDetail) {
        // 1) Converter objeto p/ array, 2) Reverter ordem
        const entries = Object.entries(
          (this.stats.data[this.formItemIndex - 1].detail as StarRatingStatsDetail).vote_distribution
        )
          .map(([key, value]) => [Number(key), value] as [number, number])
          .sort(([a], [b]) => b - a)

        return entries
      }
      return []
    },
    getStarPercentage (numberOfVotes: number, formItemIndex: number) {
      const totalResponses = this.getTotalResponses(formItemIndex)
      if (typeof totalResponses !== 'number') return ''
      if (numberOfVotes === 0) return '(0%)'
      return '(' + ((numberOfVotes * 100) / totalResponses).toFixed(1) + '%)'
    },
    /* ============== FACES RATING ============== */
    getFacesVoteDistribution (): Array<SingleFaceStats> {
      if (!this.isFaceRatingDetail) {
        return []
      }

      const distribution =
        (this.stats.data[this.formItemIndex - 1].detail as FaceRatingStatsDetail)
          .distribution

      return distribution ? [...distribution] : []
    },
    getPercentageRange (faceValue: number, totalFaces: number) {
      const percentagePerFace = 100 / totalFaces
      const minPercentage = (faceValue - 1) * percentagePerFace
      const maxPercentage = faceValue * percentagePerFace
      return { minPercentage, maxPercentage }
    },
    getEmoticon (faceIndex: number): string {
      /* const emoticons = ['mdi-emoticon', 'mdi-emoticon-happy', 'mdi-emoticon-neutral', 'mdi-emoticon-sad'] */
      const emoticons = ['EmoticonFace1', 'EmoticonFace2', 'EmoticonFace3', 'EmoticonFace4']
      return emoticons[faceIndex] || 'mdi-emoticon-neutral'
    },
    getFaceColor (faceIndex: number): string {
      const colors = ['#EF4B3F', '#EFB025', '#d2e024', '#6EBF43']
      return colors[faceIndex] || 'grey'
    },
    getAverageFaceEmoticon (): string {
      const averageFacePercentage = this.getAverageFacePercentage()
      return this.getEmoticon(averageFacePercentage)
    },
    getAverageFacePercentage (): number {
      if (this.isFaceRatingDetail) {
        const formItemDetailedStats = (this.stats.data[this.formItemIndex - 1].detail as FaceRatingStatsDetail)
        if ('average_percentage' in formItemDetailedStats) {
          return formItemDetailedStats.average_percentage
        }
      }
      return 0.0
    },
    getAverageFaceColor (): string {
      const averageFacePercentage = this.getAverageFacePercentage()
      return this.getFaceColor(averageFacePercentage)
    },
    navigate (routeName: string) {
      this.router.push({ name: routeName })
    },
    getRatingTypeName (ratingTypeID: number | null | undefined): string {
      if (ratingTypeID === null || ratingTypeID === undefined) return 'N/A'
      const item = this.ratingTypesList.find(ratingTypeObject => ratingTypeObject.id === ratingTypeID)
      return item ? item.name : 'N/A'
    },
    getRatingTypeStepsNumber (ratingTypeID: number | null | undefined): number {
      if (ratingTypeID === null || ratingTypeID === undefined) return 0
      const item = this.ratingTypesList.find(ratingTypeObject => ratingTypeObject.id === ratingTypeID)
      return item ? item.num_steps : 0
    }
  },
  computed: {
    getFormItemIconIndex (): string {
      return `mdi-numeric-${this.formItemIndex}`
    },
    isStarRatingDetail (): boolean {
      return this.stats.data[this.formItemIndex - 1] && 'vote_distribution' in this.stats.data[this.formItemIndex - 1].detail
    },
    isFaceRatingDetail (): boolean {
      return this.stats.data[this.formItemIndex - 1] && 'distribution' in this.stats.data[this.formItemIndex - 1].detail
    }
  }
})
</script>

<style lang="scss" scoped>
@import "../../../styles/colors.scss";

.form-item-container {
  padding: 1.0em 1.5em;
  border-radius: 0px 0px 15px 15px;
  display: grid;
  grid-template-rows: auto 1fr;

  .form-item-header {
    display: grid;
    grid-template-columns: auto auto;
    justify-content: start;

    .question {
      padding: 0 0.8em;
    }
  }

  .faces-item-body {
    // display: grid;
    // grid-template-rows: auto 1fr auto;
    display: flex;
    flex-direction: column;
    width: 100%;

    .overall-info-container {
      grid-row: 1;
      display: flex;
      align-items: start;
      justify-content: end;
    }

    .all-faces-container {
      grid-row: 2;
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      grid-template-rows: auto;
      align-items: center;
      padding-top: 0.6em;

      .single-face-container {
        display: grid;
        grid-template-rows: 1fr auto;
        grid-template-columns: 1fr;
        // grid-template-columns: repeat(auto-fit, minmax(min(10rem, 100%), 1fr));
        align-items: center;
        justify-items: center;
        height: auto;
        padding-right: 1.0em;

        .percentage-count-container {
          display: grid;
          grid-template-rows: auto auto;

          .response-count {
            grid-row: 2;
            /* padding: 0em 0em 0em 0.2em; */
          }

          .percentage {
            grid-row: 2;
            padding: 0em 0em 0em 0.4em;
          }
        }
      }

    }

    .submission-info-container {
      grid-row: 3;
      display: grid;
      height: 100%;
      align-items: end;
      justify-items: start;
      padding-top: 0.6em;
    }
  }

  .stars-item-body {
    display: grid;
    grid-template-columns: 10em 1fr;
    height: auto;
    padding-top: 0.4em;

    .stats-summary {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-content: flex-start;
      justify-content: center;

      height: auto;
      justify-content: center;
      padding: 0em 1.0em 0em 0em;

      .star-rating {
        justify-content: center;
      }
    }

    .stats-summary>* {
      width: 100%;
    }

    .stats-detailed {
      display: grid;
      grid-template-columns: auto 1fr 1fr;
      // height: auto;
      // display: flex;
      align-content: flex-start;

      .stats-detailed-label,
      .stats-detailed-bars,
      .stats-detailed-numbers {
        display: grid;
        grid-template-rows: repeat(5, auto);
        height: auto;
        align-items: center;
      }

      .stats-detailed-label {
        padding-right: 1.0em;
      }

      .stats-detailed-bars {
        padding-right: 1.0em;
      }

      .stats-detailed-numbers {}
    }
  }
}
</style>
